/* eslint react/prop-types: 0 */
import { useEffect, useState } from 'react';
import { Alert, Button, MenuItem, Stack, TextField } from '@mui/material';
import { fetchMethod } from '../../common/apiGate';
import { API_URL } from '../../config';
import { DASHBOARD_API_ROOT } from './dashboardContract';

const contexts = {
  asset: 'Location',
  party: 'People',
  service: 'Services',
  contract: 'Accounts'
};

export default function DashboardFieldSourceSelector({
  source,
  onChange,
  disabled
}) {
  const [fields, setFields] = useState([]);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const enabled = !!source;
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    setError(false);
    fetchMethod(
      `${API_URL}${DASHBOARD_API_ROOT}/field-sources`,
      {},
      'get',
      true
    )
      .then((response) => {
        if (!Array.isArray(response?.data?.data))
          throw new Error('Unavailable fields');
        if (active) setFields(response.data.data);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [enabled, disabled, attempt]);
  const linkedConfig = fields.find(field => field.fieldId === source?.fieldId && field.objectType === source?.objectType)?.config;
  return (
    <Stack spacing={2} sx={{ mb: enabled ? 0 : 2 }}>
      <TextField
        select
        label="Metric source"
        value={enabled ? 'field' : 'manual'}
        disabled={disabled}
        onChange={(event) =>
          onChange(
            event.target.value === 'manual'
              ? null
              : { fieldId: '', objectType: 'asset', aggregation: 'latest' }
          )
        }
      >
        <MenuItem value="manual">Manual entry</MenuItem>
        <MenuItem value="field">Layout field</MenuItem>
      </TextField>
      {enabled && (
        <>
          <Alert severity="info">
            Values come from approved layout history. Widgets show the current
            value; graph dates use your timezone. Change values through the
            layout’s correction workflow.
          </Alert>
          {error && (
            <Alert
              severity="error"
              action={
                <Button onClick={() => setAttempt((n) => n + 1)}>Retry</Button>
              }
            >
              Unable to load layout fields.
            </Alert>
          )}
          {disabled ? (
            <TextField
              label="Linked field"
              value={`${contexts[source.objectType]} · ${fields.find((field) => field.fieldId === source.fieldId && field.objectType === source.objectType)?.label || source.fieldId}`}
              disabled
            />
          ) : (
            <TextField
              select
              label="Layout field"
              value={
                source.fieldId ? `${source.objectType}:${source.fieldId}` : ''
              }
              helperText={
                !fields.length && !error
                  ? 'No numeric fields available, or fields are loading.'
                  : 'Only numeric fields in current layouts are available.'
              }
              onChange={(event) => {
                const [objectType, fieldId] = event.target.value.split(':');
                onChange({
                  fieldId,
                  objectType,
                  aggregation: objectType === 'asset' ? 'latest' : 'sum'
                }, fields.find(field => field.fieldId === fieldId && field.objectType === objectType)?.config);
              }}
            >
              {fields.map((field) => (
                <MenuItem
                  key={`${field.objectType}:${field.fieldId}`}
                  value={`${field.objectType}:${field.fieldId}`}
                >
                  {contexts[field.objectType]} · {field.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          {linkedConfig && <Stack spacing={1}>
            <TextField label="Value type" value={linkedConfig.format || 'number'} disabled />
            <TextField label="Decimal places" value={linkedConfig.decimal_places ?? 'Automatic'} disabled />
            <TextField label="Minimum value" value={linkedConfig.min ?? 'Not set'} disabled />
            <TextField label="Maximum value" value={linkedConfig.max ?? 'Not set'} disabled />
          </Stack>}
          {source.objectType !== 'asset' && (
            <TextField
              select
              label="Combine related records"
              value={source.aggregation}
              disabled={disabled}
              onChange={(event) =>
                onChange({ ...source, aggregation: event.target.value })
              }
            >
              {['sum', 'average', 'minimum', 'maximum'].map((value) => (
                <MenuItem value={value} key={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          )}
        </>
      )}
      {disabled && (
        <Alert severity="info">
          A metric’s source is fixed. Create another metric to use a different
          source.
        </Alert>
      )}
    </Stack>
  );
}
